import React, { useState, useEffect } from "react";
import "./loading-custom.css";

interface LoadingProps {
    message: string;
    tips?: string[];
}

export const LoadingComponent: React.FC<LoadingProps> = ({ message, tips }) => {
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        if (!tips || tips.length === 0) return;

        const interval = setInterval(() => {
            setIsVisible(false);

            setTimeout(() => {
                setCurrentTipIndex((prev) => (prev + 1) % tips.length);
                setIsVisible(true);
            }, 150); // Half of the transition duration for smooth fade effect
        }, 4000);

        return () => clearInterval(interval);
    }, [tips]);

    return (
        <div className="flex flex-col items-center justify-center min-h-full py-8 bg-white">
            <div className="mb-6">
                <LoadingCircleCustom />
            </div>

            {/* Message */}
            <div className="text-lg font-medium text-gray-700 mb-2 text-center max-w-md">{message}</div>

            <div className="w-full max-w-md mb-8">
                <LoadingHorizontalCustom />
            </div>

            {/* Tips Box */}
            {tips && tips.length > 0 && (
                <div className="w-full max-w-2xl px-4">
                    <div
                        className={`bg-blue-50 border border-blue-100 rounded-lg p-4 transition-opacity duration-300 ease-in-out ${
                            isVisible ? "opacity-100" : "opacity-0"
                        }`}
                    >
                        <div className="flex items-start justify-center space-x-2 text-sm text-gray-600">
                            <span className="text-blue-500 flex-shrink-0">💡</span>
                            <span>{tips[currentTipIndex]}</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Circle Loading Spinner Component
export const LoadingCircleCustom = () => {
    const loaderStyle: React.CSSProperties = {
        width: "48px",
        height: "48px",
        border: "5px solid #E3F2FD",
        borderBottomColor: "#1976D2",
        borderRadius: "50%",
        display: "inline-block",
        boxSizing: "border-box",
        animation: "rotation 2s linear infinite",
    };

    return <div style={loaderStyle}></div>;
};

// Horizontal Loading Bar Component
export const LoadingHorizontalCustom = () => {
    const loaderStyle: React.CSSProperties = {
        width: "100%",
        height: "4.8px",
        display: "inline-block",
        position: "relative",
        background: "#E3F2FD",
        overflow: "hidden",
    };

    const loaderAfterStyle: React.CSSProperties = {
        content: '""',
        width: "192px",
        height: "4.8px",
        background: "#1976D2",
        position: "absolute",
        top: "0",
        left: "0",
        boxSizing: "border-box",
        animation: "animloader 3s linear infinite",
    };

    return (
        <div style={loaderStyle}>
            <div style={loaderAfterStyle}></div>
        </div>
    );
};

export const LoadingShadowRollingCustom = () => {
    const loaderStyle: React.CSSProperties = {
        borderRadius: "50%",
        width: "1.5em",
        height: "1.5em",
        animationFillMode: "both",
        animation: "bblFadInOut 1.8s infinite ease-in-out",
        color: "#1976D2",
        fontSize: "7px",
        position: "relative",
        top: "-1rem",
        left: "-1.5rem",
        textIndent: "-9999em",
        transform: "translateZ(0)",
        animationDelay: "-0.16s",
        display: "inline-block",
    };

    const beforeAfterBaseStyle: React.CSSProperties = {
        content: '""',
        position: "absolute",
        borderRadius: "50%",
        width: "1.5em",
        height: "1.5em",
        animationFillMode: "both",
        animation: "bblFadInOut 1.8s infinite ease-in-out",
    };

    const beforeStyle: React.CSSProperties = {
        ...beforeAfterBaseStyle,

        left: "-2.5em",
        animationDelay: "-0.32s",
    };

    const afterStyle: React.CSSProperties = {
        ...beforeAfterBaseStyle,
        left: "2.5em",
        animationDelay: "0s",
    };

    return (
        <div style={loaderStyle}>
            <div style={beforeStyle}></div>
            <div style={afterStyle}></div>
        </div>
    );
};
